import {
  IconDatabase,
  IconQuote,
  IconSearch,
  IconLock,
  IconUsers,
  IconPlug,
  IconCloud,
  IconFile,
} from "@tabler/icons-react";
import { type Locale, getTranslationFn } from "../../i18n";
import { cn } from "../../lib/utils";

interface FeaturesSectionWithHoverEffectsProps {
  locale: Locale;
}

export function FeaturesSectionWithHoverEffects({
  locale,
}: FeaturesSectionWithHoverEffectsProps) {
  const t = getTranslationFn(locale);

  const features = [
    {
      title: t("Landing.toolkit.dataHeavy.title"),
      description: t("Landing.toolkit.dataHeavy.description"),
      icon: <IconDatabase className="h-8 w-8" />,
    },
    {
      title: t("Landing.toolkit.citations.title"),
      description: t("Landing.toolkit.citations.description"),
      icon: <IconQuote className="h-8 w-8" />,
    },
    {
      title: t("Landing.toolkit.privateTenant.title"),
      description: t("Landing.toolkit.privateTenant.description"),
      icon: <IconCloud className="h-8 w-8" />,
    },
    {
      title: t("Landing.toolkit.multiStep.title"),
      description: t("Landing.toolkit.multiStep.description"),
      icon: <IconSearch className="h-8 w-8" />,
    },
    {
      title: t("Landing.toolkit.integration.title"),
      description: t("Landing.toolkit.integration.description"),
      icon: <IconPlug className="h-8 w-8" />,
      soon: false,
    },
    {
      title: t("Landing.toolkit.dataIsolation.title"),
      description: t("Landing.toolkit.dataIsolation.description"),
      icon: <IconLock className="h-8 w-8" />,
    },
    {
      title: t("Landing.toolkit.export.title"),
      description: t("Landing.toolkit.export.description"),
      icon: <IconFile className="h-8 w-8" />,
    },
    {
      title: t("Landing.toolkit.team.title"),
      description: t("Landing.toolkit.team.description"),
      icon: <IconUsers className="h-8 w-8" />,
    },
  ];

  const comingSoonText = t("Landing.toolkit.comingSoon");
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4  relative z-10 py-10 max-w-7xl mx-auto">
      {features.map((feature, index) => (
        <Feature
          key={feature.title}
          {...feature}
          index={index}
          comingSoonText={comingSoonText}
        />
      ))}
    </div>
  );
}

const Feature = ({
  title,
  description,
  icon,
  soon,
  index,
  comingSoonText,
}: {
  title: string;
  soon?: boolean;
  description: string;
  icon: React.ReactNode;
  index: number;
  comingSoonText: string;
}) => {
  return (
    <div
      className={cn(
        "flex flex-col lg:border-r py-10 relative group/feature border-slate-100",
        (index === 0 || index === 4) && "lg:border-l border-slate-100",
        index < 4 && "lg:border-b border-slate-100"
      )}
    >
      {index < 4 && (
        <div className="opacity-0 group-hover/feature:opacity-100 transition duration-200 absolute inset-0 h-full w-full bg-gradient-to-t from-slate-50/50 to-transparent pointer-events-none" />
      )}
      {index >= 4 && (
        <div className="opacity-0 group-hover/feature:opacity-100 transition duration-200 absolute inset-0 h-full w-full bg-gradient-to-b from-slate-50/50 to-transparent pointer-events-none" />
      )}
      <div className="mb-6 relative z-10 px-10 text-primary/80 group-hover/feature:text-primary transition-colors duration-200">
        {icon}
      </div>
      <div className="text-lg font-bold mb-2 relative z-10 px-10">
        <div className="absolute left-0 inset-y-0 h-6 group-hover/feature:h-8 w-1 rounded-tr-full rounded-br-full bg-slate-200 group-hover/feature:bg-primary transition-all duration-200 origin-center" />
        <span className="group-hover/feature:translate-x-2 transition duration-200 inline-block text-slate-900">
          {title}
          {soon && (
            <div className="text-[10px] font-medium bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full inline-block ml-2 align-middle border border-blue-100">
              {comingSoonText}
            </div>
          )}
        </span>
      </div>
      <p className="text-sm text-slate-500 max-w-xs relative z-10 px-10 leading-relaxed group-hover/feature:text-slate-600 transition-colors duration-200">
        {description}
      </p>
    </div>
  );
};
